import { useState, useEffect, useCallback } from 'react';

export const useTheme = () => {
  const [currentTheme, setCurrentTheme] = useState('dark');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setCurrentTheme(savedTheme);
    }
    setIsLoaded(true);
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (!isLoaded) return;

    // Set data-theme attribute on document element
    document.documentElement.setAttribute('data-theme', currentTheme);
    
    // Save to localStorage
    localStorage.setItem('theme', currentTheme);

    // Broadcast theme changes so all hook instances update immediately (same tab).
    window.dispatchEvent(new CustomEvent('themechange', { detail: { theme: currentTheme } }));
  }, [currentTheme, isLoaded]);

  // Keep all useTheme hook instances in sync (same tab + cross-tab).
  useEffect(() => {
    const syncTheme = (nextTheme) => {
      if ((nextTheme === 'light' || nextTheme === 'dark') && nextTheme !== currentTheme) {
        setCurrentTheme(nextTheme);
      }
    };

    const handleThemeChange = (event) => {
      const nextTheme =
        event?.detail?.theme ||
        document.documentElement.getAttribute('data-theme') ||
        localStorage.getItem('theme');
      syncTheme(nextTheme);
    };

    const handleStorage = (event) => {
      if (event.key === 'theme') {
        syncTheme(event.newValue);
      }
    };

    window.addEventListener('themechange', handleThemeChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('themechange', handleThemeChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [currentTheme]);

  // Toggle between light and dark themes
  const toggleTheme = useCallback(() => {
    setCurrentTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  // Set specific theme
  const setTheme = useCallback((themeName) => {
    if (themeName === 'light' || themeName === 'dark') {
      setCurrentTheme(themeName);
    }
  }, []);

  return {
    currentTheme,
    isLoaded,
    toggleTheme,
    setTheme
  };
}; 